/**
 * ItemTab — tab "Item" của Production Report (`/report/batch-report/item`).
 *
 * READ-ONLY. Block "Quality Standard": item của PO (issue ∪ receive) + Standard
 * MC/Blacks/Brokens/FM lấy từ `tlc_factory.view_factory_deduction_standard_detail`
 * (factory-level, KHÔNG phải po.standard_edit) + quality_premium/price_from_g2/fx
 * (CTE premium) + description (list_and_valo). Nguồn: query mẫu
 * `batchreport_quality.txt` → `GET /factories/{fc}/batch-report/{po}/item-info`.
 */
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material'
import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react'

import {
  useGetBatchReportItemInfoQuery,
  type PoItemInfoRow,
  type RejectsByproductRow,
} from '@/store/api/factoryBomApi'

import { FilterTable, type FilterCol } from './FilterTable'
import { BlockTitle } from './reportUi'
import { COLORS } from './styleTokens'

interface Props {
  factoryCode: string
  poNo: string
  /** Giữ để parent tương thích — read-only nên không còn ý nghĩa edit. */
  canEdit?: boolean
  poOptions?: string[]
  poMeta?: Record<string, { bomCode: string; poDate: string }>
  onDirtyChange?: (dirty: boolean) => void
}

export interface ItemTabHandle {
  /** Read-only → luôn true (không có gì để lưu). Parent confirm dialog vẫn gọi. */
  saveAll: () => Promise<boolean>
}

/** Standard MC/Blacks/Brokens/FM là %. Render: "12.50%"; số âm → ngoặc "(0.45%)". */
const fmtPct = (v: number | null): string => {
  if (v == null) return ''
  const body = `${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
  return v < 0 ? `(${body})` : body
}

/** Số thường (premium/price/fx) 2 chữ số thập phân; số âm → ngoặc kế toán. */
const fmtNum2 = (v: number | null): string => {
  if (v == null) return ''
  const body = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return v < 0 ? `(${body})` : body
}

const numCell = (v: number | null, fmt: (n: number | null) => string) => (
  <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(v)}</Box>
)

const ItemTab = forwardRef<ItemTabHandle, Props>(function ItemTab(
  { factoryCode, poNo, onDirtyChange },
  ref,
) {
  const skip = !factoryCode || !poNo.trim()
  const { data, isFetching, isError } = useGetBatchReportItemInfoQuery(
    { factoryCode, poNo: poNo.trim() },
    { skip, refetchOnMountOrArgChange: true },
  )
  const rows = useMemo(() => data?.items ?? [], [data])

  // Read-only: không có dirty, saveAll no-op.
  useImperativeHandle(ref, () => ({ saveAll: async () => true }), [])
  useEffect(() => {
    onDirtyChange?.(false)
  }, [onDirtyChange])

  const stdCol = (key: 'mc' | 'blacks' | 'brokens' | 'brown' | 'fm', label: string): FilterCol<PoItemInfoRow> => ({
    key,
    label,
    width: 92,
    align: 'right',
    numeric: true,
    value: (r) => r[key],
    render: (r) => numCell(r[key], fmtPct),
  })
  const numCol = (
    key: 'qualityPremium' | 'priceVsG2' | 'fx',
    label: string,
  ): FilterCol<PoItemInfoRow> => ({
    key,
    label,
    width: 124,
    align: 'right',
    numeric: true,
    value: (r) => r[key],
    render: (r) => numCell(r[key], fmtNum2),
  })

  const cols: FilterCol<PoItemInfoRow>[] = [
    { key: 'item', label: 'Item', width: 150, frozen: true, value: (r) => r.item },
    { key: 'phase', label: 'Phase', width: 124, filter: 'checkboxes', value: (r) => r.phase },
    stdCol('mc', 'MC'),
    stdCol('blacks', 'Blacks'),
    stdCol('brokens', 'Brokens'),
    stdCol('brown', 'Brown'),
    stdCol('fm', 'FM'),
    numCol('qualityPremium', 'Quality Premium'),
    numCol('priceVsG2', 'Price vs G2 (USD)'),
    numCol('fx', 'FX'),
    { key: 'description', label: 'Description', width: 190, value: (r) => r.description },
  ]

  return (
    <Box sx={{ p: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 1, flexWrap: 'wrap' }}>
        <BlockTitle>Quality Standard</BlockTitle>
      </Stack>
      <Typography
        sx={{ mt: 0.25, mb: 0.25, fontSize: '0.72rem', color: COLORS.textMuted, fontStyle: 'italic' }}
      >
        Input &amp; target qualities from the BOM recipe, plus the PO&apos;s actual items.
        When the PO is Finished, values are frozen (logged at closing).
      </Typography>

      {isError ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          Could not load items for this PO.
        </Alert>
      ) : null}

      {skip ? (
        <EmptyState message="Select a PO to view items." />
      ) : isFetching && rows.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={22} />
        </Box>
      ) : rows.length === 0 && !isError ? (
        <EmptyState message="No items for this PO." />
      ) : rows.length > 0 ? (
        <Box sx={{ mt: 0.5 }}>
          <FilterTable
            storageKey="batchreport.item"
            columns={cols}
            rows={rows}
            rowKey={(r, i) => `${r.item ?? ''}-${i}`}
            searchPlaceholder="Search items…"
          />
        </Box>
      ) : null}

      {/* Block phụ: chỉ cột Rejects/Byproduct của bảng Quality Standard. */}
      <RejectedByproductStandard
        rows={data?.rejectsByproduct ?? []}
        loading={isFetching}
        error={isError}
      />
    </Box>
  )
})

export default ItemTab

/* ---------- Block "Rejects/Byproduct Standard" ------------------------------
 * Cột Rejects/Byproduct. Nguồn = response item-info (PO-aware): is_finished=false
 * → live (factory deduction standard); is_finished=true → value frozen từ
 * po.standard_edit["rejects/byproduct"]. field_name/parameter_code = label master. */

const fmtNum4 = (n: number | null): string => {
  if (n == null) return ''
  const body = Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 4 })
  return n < 0 ? `(${body})` : body
}

const RBP_COLS: FilterCol<RejectsByproductRow>[] = [
  // Freeze cột định danh = Field Name (sticky left → phải là cột đầu).
  { key: 'fieldName', label: 'Field Name', width: 240, frozen: true, value: (r) => r.fieldName ?? '' },
  { key: 'parameterCode', label: 'Parameter', width: 160, value: (r) => r.parameterCode ?? '' },
  {
    key: 'rejectedByproduct',
    label: 'Rejects/Byproduct',
    width: 180,
    align: 'right',
    numeric: true,
    value: (r) => r.rejectedByproduct,
    render: (r) => <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtNum4(r.rejectedByproduct)}</Box>,
  },
]

function RejectedByproductStandard({
  rows,
  loading,
  error,
}: {
  rows: RejectsByproductRow[]
  loading: boolean
  error: boolean
}) {
  const isFetching = loading
  const isError = error

  return (
    <Box sx={{ mt: 2.5 }}>
      <BlockTitle>Rejects/Byproduct Standard</BlockTitle>
      <Typography
        sx={{ mt: 0.25, mb: 0.25, fontSize: '0.72rem', color: COLORS.textMuted, fontStyle: 'italic' }}
      >
        Read-only here. To edit Rejects/Byproduct values, go to the Quality Standard page.
      </Typography>
      {isError ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          Could not load deduction standards.
        </Alert>
      ) : isFetching && rows.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={22} />
        </Box>
      ) : rows.length === 0 ? (
        <EmptyState message="No deduction standards for this factory." />
      ) : (
        <Box sx={{ mt: 0.5 }}>
          <FilterTable
            storageKey="batchreport.rbpdeduction"
            columns={RBP_COLS}
            rows={rows}
            rowKey={(r, i) => r.parameterCode || i}
            searchPlaceholder="Search parameters…"
          />
        </Box>
      )}
    </Box>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 200,
        border: `1px dashed ${COLORS.border}`,
        borderRadius: 1,
        mt: 1,
      }}
    >
      <Typography sx={{ fontSize: '0.85rem', color: COLORS.textSubtle }}>{message}</Typography>
    </Box>
  )
}
